import axios, { AxiosInstance, AxiosError } from 'axios';
import { ExchangeRequest, ExchangeAction, Signature } from '../types/exchange';

export interface RequestSenderConfig {
  baseURL: string;
  timeout?: number;
}

/**
 * RequestSender is responsible for sending HTTP requests to the Hyperliquid API
 */
export class RequestSender {
  private readonly client: AxiosInstance;

  constructor(config: RequestSenderConfig) {
    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      config => {
        // Can add logging or modification here
        return config;
      },
      error => {
        console.error('Request error:', error);
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.client.interceptors.response.use(
      response => {
        return response;
      },
      (error: AxiosError) => {
        if (error.response) {
          console.error('Response error:', {
            status: error.response.status,
            data: error.response.data,
          });
        } else if (error.request) {
          console.error('No response received:', error.request);
        } else {
          console.error('Request setup error:', error.message);
        }
        return Promise.reject(error);
      },
    );
  }

  /**
   * Send a request to the exchange endpoint
   */
  async sendExchangeRequest<T = unknown>(
    action: ExchangeAction,
    signature: Signature,
    nonce: number,
    vaultAddress?: string,
    expiresAfter?: number,
  ): Promise<T> {
    const request: ExchangeRequest = {
      action,
      nonce,
      signature,
      ...(vaultAddress && { vaultAddress }),
      ...(expiresAfter && { expiresAfter }),
    };

    const response = await this.client.post('/exchange', request);

    if (response.data.status === 'err' || response.data.status === 'error') {
      throw new Error(`Exchange error: ${JSON.stringify(response.data)}`);
    }

    return response.data;
  }

  /**
   * Send a request to the info endpoint
   */
  async sendInfoRequest<T = unknown>(type: string, params?: Record<string, unknown>): Promise<T> {
    const body = {
      type,
      ...params,
    };

    const response = await this.client.post('/info', body);
    return response.data;
  }

  /**
   * Get request for public endpoints
   */
  async get<T = unknown>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get(endpoint, { params });
    return response.data;
  }

  /**
   * Post request for public endpoints
   */
  async post<T = unknown>(endpoint: string, data: unknown): Promise<T> {
    const response = await this.client.post(endpoint, data);
    return response.data;
  }
}
